import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/pro-regular-svg-icons/faPen';

import React from 'react';
import classnames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Card } from 'ui-components/components/card';
import { CompletedStepCheckmark } from 'ui-components/components/completed-step-checkmark/completed-step-checkmark';
import { DropdownMenuType } from 'ui-components/components/dropdown-menu/dropdown-menu-type';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Input } from 'ui-components/components/input';
import { RadioCheckList } from 'ui-components/components/radio-check-list';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import MissingShipFrom from '../../../../components/shipment-configuration-widget/missing-ship-from';
import { Features } from '../../../../constants/features';
import { useEnableAddToPickingQueue } from '../../../../feature-flags/hooks/use-enable-add-to-picking-queue';
import { usePlanFeatureGate } from '../../../../feature-gates/use-plan-feature-gate';
import ShipFromDropdown from '../../../../fulfillment-planner/ship-from/ship-from-dropdown';
import { PICKING_METHODS, Steps } from '../context/constants';
import { useReadyToPickContext, useStepInfo } from '../context/ready-to-pick-context';
import { messages } from '../messages';
import style from '../ready-to-pick.module.less';

/** @typedef {import('../../../../prop-types/messages').MessagePropType} MessagePropType */

const eventSourceName = 'ReadyToPickModal';

/**
 * @param {object} props
 * @param {object} props.messages
 * @returns {JSX.Element}
 */
export const SetupOrdersCard = ({ messages: messagesProp }) => {
  const {
    batchName,
    canSetupOrdersContinue,
    doesShipFromHaveInventory,
    doesShipFromHaveNativeInventory,
    isBatch,
    pickingMethod,
    shipFromId,
    sellerShipFroms,
    setShipFromId,
    setBatchName,
    setPickingMethod,
    onSetupOrdersContinue,
    setCurrentStep,
  } = useReadyToPickContext();

  const intl = useIntl();
  const { isActiveStep, isStepComplete } = useStepInfo(Steps.verifyInformation);
  const autoGeneratedPlaceholder = intl.formatMessage(messages.autogeneratedBatchName);
  const { isFeatureEnabled: hasTotePickingFeature } = usePlanFeatureGate(Features.WarehouseMobileTotePicking);
  const isPickingQueueEnabled = useEnableAddToPickingQueue();
  const isTotePickingEnabled = hasTotePickingFeature && isPickingQueueEnabled;
  const onAddShipFromSuccess = (newShipFromId) => {
    setShipFromId(newShipFromId);
  };
  const pickingMethodText = intl.formatMessage(
    pickingMethod === PICKING_METHODS.QUEUE ? messages.addToPickingQueue : messages.addToNewBatch,
  );
  const shouldShowPickingMethods = isTotePickingEnabled && !isBatch;

  return (
    <Card
      className={classnames(style['step-card'], {
        [style.inactive]: !isActiveStep,
      })}
    >
      <FlexRow className={style['cell-row']} gap={Gap.Space16}>
        <FlexColumn className={style['card-icon']}>
          <CompletedStepCheckmark isComplete={isStepComplete} />
        </FlexColumn>
        <FlexColumn className={style['card-content']} gap={Gap.Space16}>
          <FlexRow className={style['split-title']}>
            <Text textType={TextType.H3} value={messagesProp.step1Title} />
            {!isActiveStep && (
              <Button
                className={style['header-button']}
                onClick={() => setCurrentStep(Steps.verifyInformation)}
                theme={THEME_CONSTANTS.link}
              >
                <FlexRow className={style['header-button']} gap={Gap.Space4}>
                  <FontAwesomeIcon className={style['button-icon']} icon={faPen} />
                  <Text element='span' textType={TextType.Caption} value={messages.editBatchInfo} />
                </FlexRow>
              </Button>
            )}
          </FlexRow>
          {!isActiveStep && (
            <>
              <Text
                className={style['verification-summary']}
                textType={TextType.Caption}
                value={
                  <FormattedMessage
                    values={{
                      shipFromName: sellerShipFroms.find((sf) => sf.id === shipFromId)?.name,
                    }}
                    {...messages.shipFrom}
                  />
                }
              />
              {shouldShowPickingMethods ? (
                <Text
                  className={style['verification-summary']}
                  textType={TextType.Caption}
                  value={<FormattedMessage {...messages.pickingMethodSummary} values={{ pickingMethod: pickingMethodText }} />}
                />
              ) : null}
              {isBatch || pickingMethod === PICKING_METHODS.BATCH ? (
                <Text
                  className={style['verification-summary']}
                  textType={TextType.Caption}
                  value={<FormattedMessage values={{ batchName: batchName || autoGeneratedPlaceholder }} {...messages.batchName} />}
                />
              ) : null}
            </>
          )}
          {isActiveStep ? (
            <>
              <Text textType={TextType.Description} value={messagesProp.step1Body} />
              <div>
                <Text textType={TextType.Caption} value={messages.verifyShipFromTitle} />
                {sellerShipFroms.length === 0 ? (
                  <MissingShipFrom onAddShipFromSuccess={onAddShipFromSuccess} triggerSource={eventSourceName} />
                ) : (
                  <ShipFromDropdown
                    dropdownMenuType={DropdownMenuType.Menu}
                    onChange={setShipFromId}
                    selectedShipFromId={shipFromId}
                    shipFroms={sellerShipFroms}
                  />
                )}
                {!canSetupOrdersContinue && !doesShipFromHaveInventory && (
                  <BannerMessageV2
                    content={<Text textType={TextType.Caption} value={messages.noInventoryError} />}
                    layout={BannerLayout.Inline}
                    status={BannerStatus.Error}
                  />
                )}
                {!canSetupOrdersContinue && doesShipFromHaveInventory && !doesShipFromHaveNativeInventory ? (
                  <BannerMessageV2
                    content={<Text textType={TextType.Caption} value={messages.noNativeInventoryError} />}
                    layout={BannerLayout.Inline}
                    status={BannerStatus.Error}
                  />
                ) : null}
              </div>
              {shouldShowPickingMethods ? (
                <div>
                  <Text textType={TextType.Caption} value={messages.pickingMethodTitle} />
                  <RadioCheckList>
                    <RadioCheckListItem
                      isChecked={pickingMethod === PICKING_METHODS.QUEUE}
                      name='pickingMethod'
                      onChecked={() => setPickingMethod(PICKING_METHODS.QUEUE)}
                      value={PICKING_METHODS.QUEUE}
                    >
                      <FlexColumn gap={Gap.Space4}>
                        <Text textType={TextType.Paragraph} value={messages.addToPickingQueue} />
                        <Text textType={TextType.Caption} value={messages.addToPickingQueueDescription} />
                      </FlexColumn>
                    </RadioCheckListItem>
                    <RadioCheckListItem
                      isChecked={pickingMethod === PICKING_METHODS.BATCH}
                      name='pickingMethod'
                      onChecked={() => setPickingMethod(PICKING_METHODS.BATCH)}
                      value={PICKING_METHODS.BATCH}
                    >
                      <FlexColumn gap={Gap.Space4}>
                        <Text textType={TextType.Paragraph} value={messages.addToNewBatch} />
                        <Text textType={TextType.Caption} value={messages.addToNewBatchDescription} />
                      </FlexColumn>
                    </RadioCheckListItem>
                  </RadioCheckList>
                </div>
              ) : null}
              {(!shouldShowPickingMethods || pickingMethod === PICKING_METHODS.BATCH) && (
                <Input
                  additionalInfoLabel={isBatch ? '' : <FormattedMessage {...messages.batchNameSubLabel} />}
                  label={<FormattedMessage {...messages.batchNameLabel} />}
                  onChange={(e) => {
                    setBatchName(e.target.value);
                  }}
                  placeholder={isBatch ? '' : autoGeneratedPlaceholder}
                  value={batchName}
                  version='version2'
                />
              )}
              <span>
                <Button disabled={!canSetupOrdersContinue} onClick={onSetupOrdersContinue} theme={THEME_CONSTANTS.primary}>
                  <FormattedMessage {...messages.continue} />
                </Button>
              </span>
            </>
          ) : null}
        </FlexColumn>
      </FlexRow>
    </Card>
  );
};
